import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { DashboardOwnerUser } from '@dashboard_models/dashboard-owner-user';
import { AuthService } from '@services/auth/auth.service';
import { Subject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ActionsService } from '@services/actions/actions.service';
import { takeUntil } from 'rxjs/operators';
import moment from 'moment';
import { HelperService } from '@services/helper/helper.service';
import { ActionCardService } from '@services/actions/actionCard.service';
import { DateTimeService } from '@services/date-time/date-time.service';
import { RouterLink } from '@angular/router';

import { MixpanelService } from '@services/mixpanel/mixpanel.service';
import { AW } from '@airwallet/types';

@Component({
    selector: 'app-small-actions-card',
    templateUrl: './small-overview-actions-card.component.html',
    styleUrls: ['./small-overview-actions-card.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [RouterLink]
})
export class SmallOverviewActionsCardComponent implements OnInit, OnDestroy {
    user!: DashboardOwnerUser;

    @Input() action!: AW.Operator.Action.Action;
    @Input() isSmallerCard = false;
    @Input() isSkeleton!: boolean;
    @Output() emitRemoveAction = new EventEmitter<any>();

    latestLogValue!: AW.Operator.Action.ActionLog;
    timestamp!: string;
    reversedLogKeys!: string[];

    showAdviceAction = true;
    showAlertAction = true;

    private ngUnsubscribe = new Subject<void>();

    constructor(
        protected authService: AuthService,
        private translate: TranslateService,
        private actionService: ActionsService,
        private mixpanelService: MixpanelService,
        private helperService: HelperService,
        private actionCardService: ActionCardService,
        private dateTimeService: DateTimeService
    ) {
        actionService.selectedAdviceToggled$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(value => {
            this.showAdviceAction = value;
        });
        actionService.selectedAlertToggled$$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(value => {
            this.showAlertAction = value;
        });
    }

    ngOnInit(): void {
        moment.locale(this.translate.currentLang);
        this.user = this.helperService.getDashboardOwnerUser();

        this.reversedLogKeys = this.getReversedLogKeys();

        this.latestLogValue = this.getLastestLogEntry();
        this.timestamp = this.timeSince(this.latestLogValue.timestamp);
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    showAction(): boolean {
        let hasAlert = false;
        let hasAdvice = false;
        if (this.action.active_actions) {
            for (const activeAction in this.action.active_actions) {
                if (this.action.log[activeAction].state === 'advice') {
                    hasAdvice = true;
                }
                if (this.action.log[activeAction].state !== 'advice') {
                    hasAlert = true;
                }
            }
        } else {
            if (this.latestLogValue.state === 'advice') {
                hasAdvice = true;
            }
            if (this.latestLogValue.state !== 'advice') {
                hasAlert = true;
            }
        }
        if (hasAdvice && this.showAdviceAction) {
            return true;
        } else return hasAlert && this.showAlertAction;
    }

    timeSince(timestamp: number): string {
        return this.dateTimeService.getDateAndTime(timestamp, false, false);
    }

    getLastestLogEntry(): AW.Operator.Action.ActionLog {
        const firstElementKey = Object.keys(this.action.log).reverse()[0];
        return this.action.log[firstElementKey];
    }

    getReversedLogKeys(): string[] {
        return Object.keys(this.action.log).reverse();
    }

    getState(actionLog: AW.Operator.Action.ActionLog): string {
        return this.translate.instant(`operator.actions.states.${actionLog.state}`);
    }

    getTitle(actionLog: AW.Operator.Action.ActionLog, isForLog: boolean): string {
        return this.actionCardService.getTitle(actionLog, isForLog, this.isSkeleton, this.action);
    }

    getDescription(actionLog: AW.Operator.Action.ActionLog, action: AW.Operator.Action.Action): string {
        return this.actionCardService.getDescription(actionLog, action);
    }

    onLinkClick(item_number: number) {
        this.mixpanelService.track(`Action: Actions Card ${item_number} Clicked`, { action: `action card ${item_number} from home` });
    }
}
